
import React, { useState } from "react";
import {
  FaEnvelope,
  FaPhoneAlt,
  FaMapMarkerAlt,
  FaInstagram,
  FaTelegramPlane,
  FaWhatsapp,
} from "react-icons/fa";

import Header from "../Header";
import Footer from "../Footer";
import {
  CONTACT_INFO,
  sendContactMessage,
  validateContactMessage,
} from "../../Services/contactService";

import "./Contact.css";

const initialForm = {
  name: "",
  phone: "",
  email: "",
  message: "",
};

export default function Contact({ navigateTo }) {
  const [formData, setFormData] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [statusMessage, setStatusMessage] = useState("");
  const [statusType, setStatusType] = useState("");
  const [sending, setSending] = useState(false);

  const normalizePhone = (value) => {
    return String(value || "").replace(/[^0-9]/g, "").slice(0, 11);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    const finalValue = name === "phone" ? normalizePhone(value) : value;

    setFormData((prev) => ({
      ...prev,
      [name]: finalValue,
    }));

    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }

    if (statusMessage) {
      setStatusMessage("");
      setStatusType("");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setStatusMessage("");
    setStatusType("");

    const validation = validateContactMessage(formData);

    if (!validation.isValid) {
      setErrors(validation.errors);
      return;
    }

    setSending(true);

    try {
      const result = await sendContactMessage(formData);

      if (result.success) {
        setStatusType("success");
        setStatusMessage(result.message);
        setFormData(initialForm);
        setErrors({});
      } else {
        setStatusType("error");
        setStatusMessage(result.message || "خطا در ارسال پیام");
        setErrors(result.errors || {});
      }
    } catch (error) {
      console.error("Contact form error:", error);
      setStatusType("error");
      setStatusMessage("خطا در ارسال پیام. لطفاً دوباره تلاش کنید.");
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <Header navigateTo={navigateTo} />

      <main className="contact-page">
        <section className="contact-hero">
          <span className="contact-eyebrow">تماس با نقش خیال</span>
          <h1>راه های ارتباطی شما با نقش خیال </h1>
          <p>
            برای ثبت سفارش، پیگیری خرید، همکاری هنری یا ارسال پیام، از فرم زیر
            استفاده کنید یا از راه‌های ارتباطی با ما در تماس باشید.
          </p>
        </section>

        <section className="contact-layout">
          <div className="contact-form-card">
            <div className="contact-card-header">
              <h2>ارسال پیام</h2>
              <p>
                پیام شما ثبت و بررسی خواهد شد.
              </p>
            </div>

            {statusMessage && (
              <div className={`contact-alert ${statusType}`}>
                {statusMessage}
              </div>
            )}

            <form onSubmit={handleSubmit} className="contact-form">
              <label className="contact-field">
                <span>نام و نام خانوادگی *</span>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="مثلاً پریسا احمدی"
                  disabled={sending}
                />
                {errors.name && <small>{errors.name}</small>}
              </label>

              <label className="contact-field">
                <span>شماره تماس *</span>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="09129516835"
                  dir="ltr"
                  disabled={sending}
                />
                {errors.phone && <small>{errors.phone}</small>}
              </label>

              <label className="contact-field">
                <span>ایمیل *</span>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="example@email.com"
                  dir="ltr"
                  disabled={sending}
                />
                {errors.email && <small>{errors.email}</small>}
              </label>

              <label className="contact-field full">
                <span>متن پیام *</span>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="پیام خود را اینجا بنویسید..."
                  rows="6"
                  disabled={sending}
                />
                {errors.message && <small>{errors.message}</small>}
              </label>

              <button type="submit" className="contact-submit-btn" disabled={sending}>
                {sending ? "در حال آماده‌سازی..." : "ارسال پیام ←"}
              </button>
            </form>
          </div>

          <aside className="contact-info-column">
            <div className="contact-info-card">
              <h2>اطلاعات تماس</h2>

              <div className="contact-info-item">
                <span className="contact-info-icon">
                  <FaPhoneAlt />
                </span>
                <div>
                  <h3>شماره تماس</h3>
                  <a href={`tel:${CONTACT_INFO.phone.replace("-", "")}`} dir="ltr">
                    {CONTACT_INFO.phone}
                  </a>
                </div>
              </div>

              <div className="contact-info-item">
                <span className="contact-info-icon">
                  <FaEnvelope />
                </span>
                <div>
                  <h3>ایمیل</h3>
                  <a href={`mailto:${CONTACT_INFO.email}`} dir="ltr">
                    {CONTACT_INFO.email}
                  </a>
                </div>
              </div>

              <div className="contact-info-item">
                <span className="contact-info-icon">
                  <FaMapMarkerAlt />
                </span>
                <div>
                  <h3>آدرس</h3>
                  <p>{CONTACT_INFO.address}</p>
                </div>
              </div>
            </div>

            <div className="contact-info-card">
              <h2>شبکه‌های اجتماعی</h2>

              <div className="contact-social-links">
                <a
                  href={CONTACT_INFO.instagramUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="instagram"
                >
                  <FaInstagram />
                  <span>اینستاگرام</span>
                </a>

                <a
                  href={CONTACT_INFO.telegramUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="telegram"
                >
                  <FaTelegramPlane />
                  <span>تلگرام</span>
                </a>

                <a
                  href={CONTACT_INFO.whatsappUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="whatsapp"
                >
                  <FaWhatsapp />
                  <span>واتساپ</span>
                </a>
              </div>
            </div>
          </aside>
        </section>

        <section className="contact-map-section">
          <div className="contact-map-card">
            <div className="contact-map-header">
              <h2>موقعیت ما روی نقشه</h2>
              <p>{CONTACT_INFO.address}</p>
            </div>

            <div className="contact-map-frame">
              <iframe
                title="Naghshe Khial Location"
                src={`https://www.google.com/maps?q=${encodeURIComponent(
                  CONTACT_INFO.address
                )}&output=embed`}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                allowFullScreen
              />
            </div>

            <a
              className="open-map-btn"
              href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                CONTACT_INFO.address
              )}`}
              target="_blank"
              rel="noreferrer"
            >
              باز کردن در Google Maps ←
            </a>
          </div>
        </section>
      </main>

      <Footer navigateTo={navigateTo} />
    </>
  );
}